<template>
    <promotion-details :promotionId="props.promotionId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full justify-center gap-2 md:gap-2">
            <page-header>
                Code Imports

                <template v-slot:actions>
                    <vx-button size="lg" @click.prevent="openImportModal()">
                        Import Codes <font-awesome-icon widthAuto :icon="faCirclePlus" />
                    </vx-button>
                </template>
            </page-header>

            <vx-alert v-if="recentlyCreated" color="success" :closeable="false" shadow class="mb-4">
                <template v-slot:icon>
                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                </template>
                <strong>New Import</strong> started successfully
            </vx-alert>

            <page-loading v-if="loading && !list.length"></page-loading>

            <vx-box
                v-if="!loading && !list.length"
                color="info"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                No imports yet.
                <vx-button @click.prevent="openImportModal()">
                    Import Codes <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </vx-box>

            <vx-table v-if="list.length" class="lg:grid-cols-7-auto" rowBreakpoint="lg">
                <vx-table-row isHeader>
                    <div>Status</div>
                    <div class="xl:col-span-2">Name</div>
                    <div>Rows</div>
                    <div>Errors</div>
                    <div>Created at</div>
                </vx-table-row>

                <promotion-coupon-import-row
                    v-for="(couponImport, index) in list"
                    :modelValue="list[index]"
                    :key="list[index].import_id"
                    class="min-h-[74px]"
                ></promotion-coupon-import-row>
            </vx-table>

            <div v-if="fetchParams.cursor" class="mt-8">
                <vx-button
                    @click="loadData()"
                    color="muted-dark"
                    size="lg"
                    class="grow-0"
                    data-test="load-more-btn"
                    :loading="loading"
                >
                    Load More
                    <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                </vx-button>
            </div>
        </div>

        <promotion-coupons-import-modal
            :promotionId="props.promotionId"
            v-model:visible="showImportModal"
            @uploaded="onUploaded($event)"
        ></promotion-coupons-import-modal>
    </promotion-details>
</template>

<script setup>
import PromotionDetails from '~/components/promotions/PromotionDetails.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { faCircleCheck, faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { VxAlert, VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { computed, onMounted, ref } from 'vue';
import { importsClient } from '~/services';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import PromotionCouponImportRow from '~/components/promotions/promotion-coupons-import/PromotionCouponImportRow.vue';
import PromotionCouponsImportModal from '~/components/promotions/PromotionCouponsImportModal.vue';
import { useQueryParams } from '~/composables/useQueryString';

const props = defineProps({
    promotionId: {
        type: Number,
        required: true,
    },
});

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();
const queryParams = useQueryParams();

const defaultFilters = () => ({
    cursor: null,
    forward: false,
});

const showImportModal = ref(false);

onMounted(async () => {
    fetchParams.value = defaultFilters();
    await loadData();

    if (queryParams.success && queryParams.import_id && queryParams.file_name) {
        if (!list.value.find((item) => item.import_id === queryParams.import_id)) {
            addNewImportToList(queryParams);
        }

        queryParams.success = undefined;
        queryParams.import_id = undefined;
        queryParams.file_name = undefined;
    }
});

const loadData = () => {
    const request = importsClient.getPromotionImports(Spark.state.currentTeam.id, props.promotionId, 'coupon', {
        ...fetchParams.value,
    });

    return fetchRecords(request);
};

const openImportModal = () => {
    showImportModal.value = true;
};

const onUploaded = (importData) => {
    addNewImportToList(importData);
};

const addNewImportToList = (importData) => {
    list.value.unshift({
        object: 'import',
        import_type: 'coupon',
        import_id: importData.import_id,
        file_name: importData.file_name,
        created_at: toUTCDateTimeFormat(dayjs().utc()),
    });

    list.value = list.value.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
};

const recentlyCreated = computed(() =>
    list.value[0]?.created_at ? dayjs.utc(list.value[0].created_at).isAfter(dayjs().subtract(15, 'seconds')) : false
);
</script>
